import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  TableHeaderCell,
  Text,
  H2,
  H3
} from '../../../../../components';

import { termTranslationShape } from '../../../../../propsShape';
import { DataDictionarySearch } from '../../molecules/DataDictionarySearch';

const MetricsContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
`;

const MetricsTable = styled(Table)`
  table-layout: fixed;
  border-collapse: collapse;
`;

const MetricHeaderCell = styled(TableHeaderCell)`
  width: 15%;
`;
const CalculationHeaderCell = styled(TableHeaderCell)`
  width: auto;
`;

const BaseCell = styled(TableCell)`
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;
const MetricCell = styled(BaseCell)`
  width: 25%;
`;
const CalculationCell = styled(BaseCell)`
  width: auto;
`;

const StyledH2 = styled(H2)`
  margin-bottom: 16px;
`;

const DataDictionarySearchContainer = styled.div`
  padding: 0 24px 16px 24px;
  border-bottom: 1px solid var(--border-muted);
`;

const MetricsTableContainer = styled.div`
  padding: 16px 24px;
  height: calc(100% - 73px);
  display: flex;
  flex-direction: column;
`;

const NoResultsContainer = styled.div`
  padding: 24px;
  text-align: center;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DataDictionaryMetricsView = ({ termTranslations }) => {
  const [searchKeyword, setSearchKeyword] = useState('');

  const sortedTermTranslations = useMemo(
    () =>
      [...termTranslations].sort((a, b) => {
        const termA = a.termList[0]?.toLowerCase() || '';
        const termB = b.termList[0]?.toLowerCase() || '';
        return termA.localeCompare(termB);
      }),
    [termTranslations]
  );

  const filteredTermTranslations = useMemo(
    () =>
      sortedTermTranslations.filter(({ termList, displayedDescription }) => {
        const keyword = searchKeyword.toLowerCase();
        return (
          termList.some((term) => term.toLowerCase().startsWith(keyword)) ||
          (displayedDescription && displayedDescription.toLowerCase().startsWith(keyword))
        );
      }),
    [sortedTermTranslations, searchKeyword]
  );

  return (
    <MetricsContainer>
      <DataDictionarySearchContainer>
        <DataDictionarySearch onChange={setSearchKeyword} />
      </DataDictionarySearchContainer>
      <MetricsTableContainer>
        <StyledH2>Metrics</StyledH2>
        {filteredTermTranslations.length > 0 ? (
          <MetricsTable>
            <TableHead>
              <TableRow>
                <MetricHeaderCell>
                  <Text as='span' $bold $color='var(--content-subtitle)'>
                    Metric
                  </Text>
                </MetricHeaderCell>
                <CalculationHeaderCell>
                  <Text as='span' $bold $color='var(--content-subtitle)'>
                    Calculation
                  </Text>
                </CalculationHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTermTranslations.map(({ id, termList, displayedDescription }) => (
                <TableRow key={id}>
                  <MetricCell>
                    {termList.map((term) => (
                      <React.Fragment key={term}>
                        <Text as='span' $bold>
                          {term}
                        </Text>
                        <br />
                      </React.Fragment>
                    ))}
                  </MetricCell>
                  <CalculationCell>
                    <Text as='span'>{displayedDescription}</Text>
                  </CalculationCell>
                </TableRow>
              ))}
            </TableBody>
          </MetricsTable>
        ) : (
          <NoResultsContainer>
            <H3>No matching metrics found</H3>
          </NoResultsContainer>
        )}
      </MetricsTableContainer>
    </MetricsContainer>
  );
};

DataDictionaryMetricsView.propTypes = {
  termTranslations: PropTypes.arrayOf(termTranslationShape).isRequired
};

export { DataDictionaryMetricsView };
